/**
 * video-to-sound/mixer.js — window.VideoSoundMixer
 *
 * The page-side sound of /video-to-sound:
 *
 *   decode(url)            a stored take/music (MP3 preview or WAV master) → AudioBuffer
 *   buildTrack(…)          the soundtrack of the prepared range, scene by scene
 *                          from whichever take the person chose for it — a
 *                          25 ms cross-fade where the source changes, a straight
 *                          copy where it does not (one take stays sample-exact)
 *   player(video)          plays that track through Web Audio IN SYNC with the
 *                          <video>: restarted on play/seek/rate change, output
 *                          latency compensated, drift corrected; the original
 *                          sound stays the <video>'s own (volume per region)
 *   render(…)              the final mix for export, whole video length:
 *                          original outside the range untouched, inside it at
 *                          the chosen level, AI sound + music on top, fades
 *   wav(buffer)            16-bit PCM WAV blob
 */
(function (w) {
    'use strict';
    if (w.VideoSoundMixer) return;

    var XF = 0.025;
    // Cross-fade when the heard take is swapped during playback.
    var SWAP = 0.03;

    function ctx() {
        if (!w.__vsAudioCtx) {
            var C = w.AudioContext || w.webkitAudioContext;
            w.__vsAudioCtx = new C({ latencyHint: 'playback' });
        }
        return w.__vsAudioCtx;
    }

    async function decode(url) {
        var r = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, redirect: 'error' });
        if (!r.ok) { var e = new Error('fetch_' + r.status); e.status = r.status; throw e; }
        var ab = await r.arrayBuffer();
        return await new Promise(function (res, rej) {
            var p = ctx().decodeAudioData(ab, res, rej);
            if (p && p.then) p.then(res, rej);
        });
    }

    /**
     * @param spanLen  seconds of the prepared range
     * @param scenes   [{start,end}] relative to the range
     * @param pick     function(i) → {buf:AudioBuffer, at:seconds of range-start inside buf} | null
     */
    function buildTrack(spanLen, scenes, pick, sr) {
        sr = sr || 48000;
        var n = Math.max(1, Math.round(spanLen * sr));
        var out = new AudioBuffer({ length: n, numberOfChannels: 2, sampleRate: sr });
        var L = out.getChannelData(0), R = out.getChannelData(1);
        var prevSrc = null;
        scenes.forEach(function (s, i) {
            var p = pick(i);
            var a = Math.round(s.start * sr), b = Math.min(n, Math.round(s.end * sr));
            if (!p || !p.buf || b <= a) { prevSrc = null; return; }
            var src = p.buf, ssr = src.sampleRate;
            var c0 = src.getChannelData(0), c1 = src.numberOfChannels > 1 ? src.getChannelData(1) : c0;
            var fadeIn = (prevSrc && prevSrc !== src) ? Math.round(XF * sr) : 0;
            var fadeOut = 0;
            var next = scenes[i + 1] ? pick(i + 1) : null;
            if (next && next.buf && next.buf !== src) fadeOut = Math.round(XF * sr);
            for (var j = a; j < b; j++) {
                var srcIdx = Math.round((p.at + j / sr) * ssr);
                if (srcIdx < 0 || srcIdx >= src.length) continue;
                var g = 1;
                if (fadeIn && j - a < fadeIn) g *= Math.sin(0.5 * Math.PI * (j - a) / fadeIn);
                if (fadeOut && b - j <= fadeOut) g *= Math.sin(0.5 * Math.PI * (b - j) / fadeOut);
                L[j] += c0[srcIdx] * g;
                R[j] += c1[srcIdx] * g;
            }
            prevSrc = src;
        });
        return out;
    }

    function player(video, opts) {
        opts = opts || {};
        var ac = ctx();
        var master = ac.createGain(), aiG = ac.createGain(), muG = ac.createGain();
        aiG.connect(master); muG.connect(master); master.connect(ac.destination);
        var bufs = { ai: null, music: null }, spanStart = 0, spanEnd = 0;
        var gains = { ai: 1, music: 0.35, orig: 0 }, mode = 'none';
        // Each start plays through its own pair of fade gains (`bus`). A new
        // take replaces the heard one by a short cross-fade at the SAME buffer
        // position, taken from the running sources' anchor, not from the coarse
        // <video> clock — comparing engines is switching while it plays, and a
        // cut-and-restart there was a click and a 20 ms hole on every switch.
        var srcs = [], bus = null, anchor = null, timer = null;

        function lat() { return (ac.outputLatency || 0) + (ac.baseLatency || 0); }
        function newBus() { var a = ac.createGain(), m = ac.createGain(); a.connect(aiG); m.connect(muG); return { ai: a, music: m }; }
        function ramp(b, from, to, t) {
            [b.ai, b.music].forEach(function (n) { n.gain.cancelScheduledValues(t); n.gain.setValueAtTime(from, t); n.gain.linearRampToValueAtTime(to, t + SWAP); });
        }
        function dropBus(b) { try { b.ai.disconnect(); b.music.disconnect(); } catch (_) {} }
        /* `at` — fade the current sources out from that context time instead of cutting them. */
        function stop(at) {
            var old = srcs, ob = bus;
            srcs = []; bus = null; anchor = null;
            if (!ob) return;
            if (typeof at === 'number' && old.length) {
                ramp(ob, 1, 0, at);
                old.forEach(function (s) { try { s.stop(at + SWAP + 0.01); } catch (_) {} });
                setTimeout(function () { dropBus(ob); }, (at - ac.currentTime + SWAP + 0.2) * 1000);
                return;
            }
            old.forEach(function (s) { try { s.stop(); } catch (_) {} });
            dropBus(ob);
        }
        function start(swap) {
            var prev = swap === true && srcs.length && anchor && !video.paused && mode === 'ai' ? anchor : null;
            var rate = video.playbackRate || 1, now, rel;
            if (prev) {
                now = ac.currentTime + 0.01;
                rel = prev.rel + (now - prev.ctxT) * prev.rate;
                stop(now);
            } else {
                stop();
                if (video.paused || mode !== 'ai') return;
                if (ac.state === 'suspended') { try { ac.resume(); } catch (_) {} }
                now = ac.currentTime + 0.02;
                rel = (video.currentTime - spanStart) + lat() * rate;
            }
            var b = newBus();
            bus = b;
            if (prev) ramp(b, 0, 1, now);
            [['ai', b.ai], ['music', b.music]].forEach(function (p) {
                var buf = bufs[p[0]];
                if (!buf) return;
                if (rel >= buf.duration) return;
                var s = ac.createBufferSource();
                s.buffer = buf; s.playbackRate.value = rate; s.connect(p[1]);
                if (rel >= 0) s.start(now, rel);
                else s.start(now + (-rel) / rate, 0);
                srcs.push(s);
            });
            anchor = { ctxT: now, rel: rel, rate: rate };
        }
        function applyGains() {
            aiG.gain.value = gains.ai;
            muG.gain.value = gains.music;
            master.gain.value = mode === 'ai' ? 1 : 0;
            var t = video.currentTime, inside = t >= spanStart - 0.05 && t <= spanEnd + 0.05;
            if (mode === 'none') { video.muted = true; }
            else if (mode === 'orig') { video.muted = false; video.volume = 1; }
            else { var v = inside ? gains.orig : (opts.hasOrig ? 1 : 0); video.muted = v <= 0.001; video.volume = Math.max(0, Math.min(1, v)); }
        }
        function drift() {
            if (!anchor || video.paused || mode !== 'ai') return;
            var expect = anchor.rel + (ac.currentTime - anchor.ctxT) * anchor.rate;
            var want = (video.currentTime - spanStart) + lat() * (video.playbackRate || 1);
            if (Math.abs(expect - want) > 0.08) start();
        }
        var on = {
            play: function () { start(); }, playing: function () { start(); }, seeked: function () { start(); }, ratechange: function () { start(); },
            pause: function () { stop(); }, seeking: function () { stop(); }, waiting: function () { stop(); }, emptied: function () { stop(); },
            timeupdate: function () { applyGains(); drift(); }
        };
        Object.keys(on).forEach(function (k) { video.addEventListener(k, on[k]); });
        timer = setInterval(drift, 1000);

        return {
            // A new take while it plays: cross-faded in place (start(true)); otherwise a plain start.
            setBuffers: function (b, s0, s1) {
                var same = (s0 || 0) === spanStart;
                bufs.ai = b.ai || null; bufs.music = b.music || null; spanStart = s0 || 0; spanEnd = s1 || 0;
                start(same);
            },
            setGains: function (g) { Object.assign(gains, g); applyGains(); },
            setMode: function (m) { mode = m; applyGains(); start(); },
            mode: function () { return mode; },
            destroy: function () {
                stop(); clearInterval(timer);
                Object.keys(on).forEach(function (k) { video.removeEventListener(k, on[k]); });
                try { master.disconnect(); } catch (_) {}
            }
        };
    }

    /**
     * The final mix for export — length of the WHOLE video.
     * o: { duration, spanStart, spanEnd, ai, music, orig (full-length buffer or null),
     *      origStartSec (where the decoded original belongs on the movie's clock),
     *      gains:{ai,music,orig}, fadeIn, fadeOut }
     */
    async function render(o) {
        var sr = 48000, len = Math.max(1, Math.ceil(o.duration * sr));
        var oc = new OfflineAudioContext({ numberOfChannels: 2, length: len, sampleRate: sr });
        var s0 = o.spanStart, s1 = o.spanEnd;

        if (o.orig) {
            var src = oc.createBufferSource(), g = oc.createGain();
            src.buffer = o.orig; src.connect(g); g.connect(oc.destination);
            var go = Math.max(0, Math.min(2, o.gains.orig));
            // Outside the voiced range the original is left as it was; inside it
            // it sits at the chosen level, with 150 ms ramps so the edge is not a click.
            g.gain.setValueAtTime(1, 0);
            if (s0 > 0.15) { g.gain.setValueAtTime(1, s0 - 0.15); }
            g.gain.linearRampToValueAtTime(go, Math.max(0.001, s0));
            g.gain.setValueAtTime(go, Math.max(0.002, s1 - 0.001));
            g.gain.linearRampToValueAtTime(1, s1 + 0.15);
            // The decoded original starts at its track's first sample; the
            // movie plays that sample origStartSec after the picture.
            src.start(Math.max(0, +o.origStartSec || 0));
        }
        [['ai', o.gains.ai], ['music', o.gains.music]].forEach(function (p) {
            var b = o[p[0]];
            if (!b || !(p[1] > 0)) return;
            var s = oc.createBufferSource(), g = oc.createGain();
            s.buffer = b; s.connect(g); g.connect(oc.destination);
            var fi = o.fadeIn ? Math.min(1.0, (s1 - s0) / 4) : 0.01, fo = o.fadeOut ? Math.min(1.5, (s1 - s0) / 4) : 0.01;
            g.gain.setValueAtTime(0, s0);
            g.gain.linearRampToValueAtTime(p[1], s0 + fi);
            g.gain.setValueAtTime(p[1], Math.max(s0 + fi, s1 - fo));
            g.gain.linearRampToValueAtTime(0, s1);
            s.start(s0, 0, s1 - s0);
        });
        var out = await oc.startRendering();
        // Keep the peak under −1 dBFS: AI sound on top of an original track can
        // add up past full scale, and a clipped soundtrack is the one thing a
        // video editor will not forgive.
        var peak = 0;
        for (var c = 0; c < out.numberOfChannels; c++) {
            var d = out.getChannelData(c);
            for (var i = 0; i < d.length; i++) { var a = d[i] < 0 ? -d[i] : d[i]; if (a > peak) peak = a; }
        }
        var ceil = Math.pow(10, -1 / 20);
        if (peak > ceil) {
            var k = ceil / peak;
            for (var c2 = 0; c2 < out.numberOfChannels; c2++) { var d2 = out.getChannelData(c2); for (var j = 0; j < d2.length; j++) d2[j] *= k; }
        }
        return out;
    }

    function wav(buf) {
        var ch = buf.numberOfChannels, sr = buf.sampleRate, n = buf.length;
        var bytes = 44 + n * ch * 2, ab = new ArrayBuffer(bytes), v = new DataView(ab);
        function str(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
        str(0, 'RIFF'); v.setUint32(4, bytes - 8, true); str(8, 'WAVE'); str(12, 'fmt ');
        v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, ch, true); v.setUint32(24, sr, true);
        v.setUint32(28, sr * ch * 2, true); v.setUint16(32, ch * 2, true); v.setUint16(34, 16, true);
        str(36, 'data'); v.setUint32(40, n * ch * 2, true);
        var chans = []; for (var c = 0; c < ch; c++) chans.push(buf.getChannelData(c));
        var o = 44;
        for (var i = 0; i < n; i++) for (var c3 = 0; c3 < ch; c3++) {
            var x = Math.max(-1, Math.min(1, chans[c3][i]));
            v.setInt16(o, x < 0 ? x * 0x8000 : x * 0x7FFF, true); o += 2;
        }
        return new Blob([ab], { type: 'audio/wav' });
    }

    w.VideoSoundMixer = { ctx: ctx, decode: decode, buildTrack: buildTrack, player: player, render: render, wav: wav };
})(window);
